import { ElectronProperties } from '../global-properties/global-properties';
import type {
    ElectronBridge as ElectronBridgeType,
    ElectronDexMetadata as ElectronDexMetadataType,
} from '@m43/bridge-interface';
import {
    WorkspaceWindowTypeEnumType,
    ConnectionStatusEnumType,
    FrontendConnectionStatusDto,
    ConnectionStatusView,
} from '@mark43/cad-api';
import { WindowActionHandler } from '@m43/action-broadcaster';
import { CadNextAction } from '../action-broadcaster/types';
import { Systeminformation } from 'systeminformation';
import isElectron from 'is-electron';

export async function getElectronGlobalProperties(): Promise<ElectronProperties> {
    if (electronRewriteBridge) {
        return await electronRewriteBridge.requestGlobalProperties();
    } else {
        const globalProperties = await oldElectronApi.getGlobalProperties();
        // our ipc call to get global properties assigns cad-next properties to special property names/objects,
        // so we "normalize" them here to have a consistent interface between electron and web
        return {
            ELECTRON_MAIN_VERSION: globalProperties.RELEASE_VERSION,
        };
    }
}

// we dont put the ElectronBridge type on window in global.d.ts so that people
// don't access it on the window and access it from this file instead, so we have
// to assert the type here
const electronRewriteBridge = (
    window as {
        electronBridge?: ElectronBridgeType;
    }
).electronBridge;

type IsOnlineCallback = (connectionStatus: ConnectionStatusEnumType) => void;

type OldElectronApi = {
    args: {
        '--window-title': string;
        '--app-name': string;
        '--workspace-window-id': number;
        '--workspace-window-type': WorkspaceWindowTypeEnumType;
        '--client-session-id': string;
        '--connection-status': ConnectionStatusEnumType;
        '--mark43-ping-status': ConnectionStatusEnumType;
    };
    getGlobalProperties: () => Promise<{
        RELEASE_VERSION: string;
    }>;
    getAuthHeaders: () => Promise<{
        Authorization?: string;
        AuthToken?: string;
        Cookie?: string;
        'Content-Type'?: string;
        Accept?: string;
        'x-m43-api-version'?: string;
        'x-cad-app-mode'?: string;
    }>;
    getUrlPrefix: () => Promise<string>;
    openUrlInBrowser: (url: string) => void;
    getDexMetadata: () => Promise<{ deviceName: string; dexStateUserId: number | undefined }>;
    getAssignedCallTakerStationId: () => Promise<number | undefined>;
    requestToPlayAudioAlert: () => Promise<boolean>;
    downloadFile: (download: { url: string; fileName: string }) => Promise<void>;
    zipAndDownloadFiles: (
        downloads: {
            url: string;
            fileName: string;
        }[]
    ) => Promise<void>;
    actionBroadcasterInterface: WindowActionHandler<CadNextAction> & {
        registerSelfMultipleWindowTypes?: (windowTypes: string[]) => void;
    };
    getErrorReportMeta: () => Promise<Record<string, string>>;
    getSystemInformation: () => Promise<{
        osInfo: Systeminformation.OsData;
        cpu: Systeminformation.CpuData;
        memory: Systeminformation.MemData;
        graphics: Systeminformation.GraphicsData;
        processes: Systeminformation.ProcessesData;
        networkInterfaces: Systeminformation.NetworkInterfacesData;
        wifiInterfaces: Systeminformation.WifiInterfaceData;
        wifiConnections: Systeminformation.WifiConnectionData;
    }>;
    getErrorReportScreenshots: () => Promise<
        | 'ERROR_GETTING_SCREENSHOTS'
        | {
              title: string;
              image: Electron.NativeImage;
              data: string;
          }[]
    >;
    registerIsOnlineCallback: (callback: IsOnlineCallback) => void;
    setFrWindowSize: undefined | (() => void);
    getConnectionStatusDtos: undefined | (() => Promise<FrontendConnectionStatusDto[]>);
    storeConnectionStatusDto: undefined | ((dto: FrontendConnectionStatusDto) => Promise<void>);
    deleteConnectionStatusDtos: undefined | ((ids: string[]) => Promise<void>);
    requestWorkspaceWindows:
        | undefined
        | (() => Promise<{ workspaceWindowType: WorkspaceWindowTypeEnumType }>);
    sendRendererVersion: undefined | ((rendererVersion: string) => void);
    registerConnectionStatusViewHandler:
        | undefined
        | ((callback: (view: ConnectionStatusView) => void) => void);
};

const oldElectronApi: OldElectronApi = {
    actionBroadcasterInterface: (window as unknown as OldElectronApi).actionBroadcasterInterface,
    args: (window as unknown as OldElectronApi).args,
    deleteConnectionStatusDtos: (window as unknown as OldElectronApi).deleteConnectionStatusDtos,
    downloadFile: (window as unknown as OldElectronApi).downloadFile,
    zipAndDownloadFiles: (window as unknown as OldElectronApi).zipAndDownloadFiles,
    getAssignedCallTakerStationId: (window as unknown as OldElectronApi)
        .getAssignedCallTakerStationId,
    getAuthHeaders: (window as unknown as OldElectronApi).getAuthHeaders,
    getConnectionStatusDtos: (window as unknown as OldElectronApi).getConnectionStatusDtos,
    getDexMetadata: (window as unknown as OldElectronApi).getDexMetadata,
    getErrorReportMeta: (window as unknown as OldElectronApi).getErrorReportMeta,
    getErrorReportScreenshots: (window as unknown as OldElectronApi).getErrorReportScreenshots,
    getGlobalProperties: (window as unknown as OldElectronApi).getGlobalProperties,
    getSystemInformation: (window as unknown as OldElectronApi).getSystemInformation,
    getUrlPrefix: (window as unknown as OldElectronApi).getUrlPrefix,
    openUrlInBrowser: (window as unknown as OldElectronApi).openUrlInBrowser,
    registerConnectionStatusViewHandler: (window as unknown as OldElectronApi)
        .registerConnectionStatusViewHandler,
    registerIsOnlineCallback: (window as unknown as OldElectronApi).registerIsOnlineCallback,
    requestToPlayAudioAlert: (window as unknown as OldElectronApi).requestToPlayAudioAlert,
    requestWorkspaceWindows: (window as unknown as OldElectronApi).requestWorkspaceWindows,
    sendRendererVersion: (window as unknown as OldElectronApi).sendRendererVersion,
    setFrWindowSize: (window as unknown as OldElectronApi).setFrWindowSize,
    storeConnectionStatusDto: (window as unknown as OldElectronApi).storeConnectionStatusDto,
};

export type ElectronDexMetadata = ElectronDexMetadataType;

export const electronBridge: undefined | ElectronBridgeType | OldElectronApi = !isElectron()
    ? undefined
    : electronRewriteBridge ?? oldElectronApi;
